import {isEqual, mapValues} from 'lodash';
import {createStructuredSelector as reselectCreateStructuredSelector, lruMemoize, Selector} from 'reselect';

import {isShallowEqual} from '../../helpers/utilities/objectHelpers';
import {ActionBase} from '../actionTypes';
import {CommonAppState} from '../commonAppState';
import {CommonThunkAction, CommonThunkDispatch} from '../commonAppThunk';
import {instrumentedCreateSelectorCreator} from './selectorInstrumentation';

/** Selector creator. */
export const createCommonSelector = instrumentedCreateSelectorCreator(lruMemoize);

/** Selector creator that deeply compares arguments. */
export const createCommonDeepEqualSelector = instrumentedCreateSelectorCreator(lruMemoize, isEqual);

/** Selector creator that shallowly compares arguments. */
export const createCommonShallowEqualSelector = instrumentedCreateSelectorCreator(lruMemoize, isShallowEqual);

/** Selector creator that uses the default/strict comparison on all arguments except the first. */
export const createCommonTailStrictEqualSelector = instrumentedCreateSelectorCreator(tailMemoize);

type AnySelector<P> = Selector<any, any, [P]>;
type FunctionMap<P> = Record<string, AnySelector<P>>;
type ResolvedFunctionMap<P, T extends FunctionMap<P>> = {[K in keyof T]: ReturnType<T[K]>};

/** Saner MapStateToProps + CreateStructureSelector. */
export function makeCommonMapStateToProps<P = {}, State extends CommonAppState = CommonAppState>() {
  return function mstpMaker<T extends FunctionMap<P>>(
    makeSelectors: () => T,
  ): () => Selector<State, ResolvedFunctionMap<P, T>, [P]> {
    // @ts-expect-error Allowing TS to infer this type results in a stack overflow, so we
    // manually type the return value instead.
    return () => reselectCreateStructuredSelector(makeSelectors());
  };
}

type AnonymousFunction = (...args: any) => any;
type UnwrappedThunkAction<T> = T extends CommonThunkAction<infer R, any, any> ? R : T;
export type UnwrappedActionCreator<T extends AnonymousFunction> = (
  ...args: Parameters<T>
) => UnwrappedThunkAction<ReturnType<T>>;

export function makeCommonMapDispatchToProps<
  P = {},
  State extends CommonAppState = CommonAppState,
  Action extends ActionBase = ActionBase,
>() {
  return function mdtpMaker<T extends {[K: string]: AnonymousFunction}>(makeActionCreators: (props: P) => T) {
    return (
      dispatch: CommonThunkDispatch<State, Action>,
      props: P,
    ): {[K in keyof T]: UnwrappedActionCreator<T[K]>} => {
      const actionCreators = makeActionCreators(props);
      return mapValues(
        actionCreators,
        (value) =>
          (...args: Array<any>) =>
            dispatch(value(...args)),
      );
    };
  };
}

/*
 * Helpers.
 */

/** Default equality check. */
export function isStrictEqual(a: any, b: any) {
  return a === b;
}

// Adapted from reselect's areArgumentsShallowlyEqual, but skips the first argument.
function areTailArgumentsShallowlyEqual(
  equalityCheck: (a: any, b: any) => boolean,
  prev: IArguments,
  next: IArguments,
) {
  if (prev === null || next === null || prev.length !== next.length) {
    return false;
  }

  // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
  const {length} = prev;
  for (let i = 1; i < length; i++) {
    if (!equalityCheck(prev[i], next[i])) {
      return false;
    }
  }

  return true;
}

// Adapted from reselect's lruMemoize, but uses areTailArgumentsShallowlyEqual.
/* eslint-disable prefer-rest-params */
// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function tailMemoize<F extends Function>(func: F, equalityCheck = isStrictEqual): F {
  let lastArgs: IArguments | null = null;
  let lastResult: any;

  // Reference arguments instead of spreading them for performance reasons.
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return function tailMemoizedFunc() {
    // Apply arguments instead of spreading for performance.
    const result =
      !lastArgs || !areTailArgumentsShallowlyEqual(equalityCheck, lastArgs, arguments)
        ? func(...arguments)
        : lastResult;

    lastArgs = arguments;
    lastResult = result;
    return result;
  } as any;
}

/* eslint-enable prefer-rest-params */
/** Find the resulting state props from a MapStateToProps method. */
export type ResolveStateProps<TSelector extends () => Selector<any, any, any>> = ReturnType<
  ReturnType<TSelector>
>;
